import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { AddressField, AddressValue } from '@/components/form/AddressInput';
import { TsForm } from '@/components/form/Form';
import { PhoneNumberField, PhoneNumberValue } from '@/components/form/PhoneNumberField';
import { SelectInputOption } from '@/components/form/SelectForm';
import { SelectInputField } from '@/components/form/SelectInput';
import { SubmitCancelButtons } from '@/components/form/SubmitCancelButtons';
import { TextField } from '@/components/form/TextInput';
import { TSTitleCard } from '@/components/shared/card/Card';
import { mapAddress } from '@/helpers/addressHelper';
import { denormalizePhoneNumber, inferPhoneRegion, normalizePhoneNumber } from '@/helpers/phoneHelper';
import { handleMutationResult } from '@/helpers/toastHelper';
import { handleError } from '@/services/gql/config/URQLProvider';
import {
  Location,
  useGetLocationsQuery,
  useGetOrganisationHierarchyQuery,
  useUpdateLocationMutation
} from '@/services/gql/graphql.generated';
import { useHolidayRegions, useTenantDefaultHolidayRegionLabel } from '@/services/holiday/useHolidayRegions';
import { useDivisionForLocation } from '@/services/location/useDivisionForLocation';
import { createErrorToast } from '@/services/notification/ToastService';
import { strings } from '@/services/translation/strings';
import { FunctionComponent, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { GiCog } from 'react-icons/gi';
import { useNavigate } from 'react-router-dom';
import adminStrings from '../../admin.translations';

interface AdminEditLocationDetailsCardProps {
  location: Location;
  onUpdated: () => void;
}

interface EditLocationFormValues {
  name: string;
  division: string;
  holidayRegion: string;
  externalId: string;
  phoneNumber: PhoneNumberValue;
  address: AddressValue;
}

const AdminEditLocationDetailsCard: FunctionComponent<AdminEditLocationDetailsCardProps> = ({
  location,
  onUpdated
}) => {
  const division = useDivisionForLocation(location.id);

  const initialRegion = inferPhoneRegion(location.phoneNumber);

  const [{ fetching }, updateLocation] = useUpdateLocationMutation();

  const [{ data: hierarchyData, error: hierarchyError }] = useGetOrganisationHierarchyQuery();
  const [{ data: locationsData, error: locationsError }] = useGetLocationsQuery();

  if (hierarchyError) handleError(hierarchyError);
  if (locationsError) handleError(locationsError);

  const divisionOptions = useMemo<SelectInputOption[]>(() => {
    const rootDivision = hierarchyData?.getOrganisationHierarchy;
    if (!rootDivision) return [];

    const extractOptions = (division: any): SelectInputOption[] => {
      if (!division) return [];

      const currentOption = {
        id: division.id,
        name: division.name
      };

      const childOptions = (division.children || []).flatMap(extractOptions);

      return [currentOption, ...childOptions];
    };

    const mappedOptions = extractOptions(rootDivision);

    return [{ id: '', name: adminStrings.locationsSection.addDialog.divisionPlaceholder }, ...mappedOptions];
  }, [hierarchyData]);

  const holidayRegionOptions = useHolidayRegions();
  const tenantDefaultLabel = useTenantDefaultHolidayRegionLabel(
    adminStrings.locationsSection.addDialog.inheritOption,
    adminStrings.locationsSection.addDialog.inheritOption
  );
  const holidayRegionSelectOptions = useMemo<SelectInputOption[]>(
    () => [
      { id: '', name: tenantDefaultLabel },
      ...holidayRegionOptions.map(option => ({ id: option.value, name: option.name }))
    ],
    [holidayRegionOptions, tenantDefaultLabel]
  );

  const methods = useForm<EditLocationFormValues>({
    defaultValues: {
      name: location.name,
      division: division?.name,
      holidayRegion: holidayRegionSelectOptions.find(h => h.id === location.holidayRegionCode)?.name ?? '',
      externalId: location.externalId,
      phoneNumber: { number: denormalizePhoneNumber(location.phoneNumber, initialRegion) ?? '', region: initialRegion },
      address: mapAddress(location.address)
    }
  });

  useEffect(() => {
    methods.reset({
      name: location.name,
      division: division?.name,
      holidayRegion:
        holidayRegionSelectOptions.find(h => h.id === location.holidayRegionCode)?.name ??
        holidayRegionSelectOptions[0].name,
      externalId: location.externalId,
      phoneNumber: {
        number: denormalizePhoneNumber(location.phoneNumber, initialRegion) ?? '',
        region: initialRegion
      },
      address: mapAddress(location.address)
    });
  }, [location]);

  const onSubmit = async (values: EditLocationFormValues) => {
    const divisionId = divisionOptions.find(d => d.name === values.division)?.id;

    if (!divisionId) {
      createErrorToast(`Division id not found for ${values.division}`);
      return;
    }

    const holidayRegionId = holidayRegionSelectOptions.find(h => h.name === values.holidayRegion)?.id;

    const result = await updateLocation({
      location: {
        id: location.id,
        name: values.name,
        active: location.active,
        divisionId: divisionId,
        holidayRegionCode: holidayRegionId,
        phoneNumber: normalizePhoneNumber(values.phoneNumber.number, values.phoneNumber.region) || undefined,
        externalId: values.externalId,
        address: {
          line1: values.address.line1,
          line2: values.address.line2,
          city: values.address.city,
          country: values.address.country,
          postcode: values.address.postcode
        }
      }
    });

    await handleMutationResult(result, {
      successMessage: adminStrings.locationsSection.editLocation.successMessage(values.name),
      errorMessage: adminStrings.locationsSection.editLocation.errorMessage(location.name),
      onSuccess: data => {
        onUpdated();
      }
    });
  };

  const navigate = useNavigate();
  const handleClose = () => {
    methods.reset();
    navigate('/admin/locations');
  };

  return (
    <TSTitleCard
      title={adminStrings.locationsSection.editLocation.title(location.name)}
      icon={GiCog}
      className="w-full"
    >
      <SubmissionErrorBoundary>
        <TsForm methods={methods} onSubmit={onSubmit}>
          <div className="flex flex-col space-y-3 w-full">
            <SelectInputField
              name="division"
              id="division"
              label={adminStrings.locationsSection.addDialog.division}
              options={divisionOptions}
              rules={{ required: adminStrings.locationsSection.addDialog.divisionRequired }}
              required
            />

            <TextField
              label={adminStrings.locationsSection.editLocation.name}
              name="name"
              id="name"
              placeholder={adminStrings.locationsSection.editLocation.locationName}
              customValidatorMessage={methods.formState.errors.name?.message}
              rules={{
                validate: (value: string) => {
                  const isDuplicate = locationsData?.locations?.some(l => l?.name === value && l?.id !== location.id);
                  if (isDuplicate) return adminStrings.locationsSection.editLocation.uniqueName;
                  return true;
                }
              }}
              required
            />

            <TextField
              label={adminStrings.locationsSection.editLocation.externalId}
              name="externalId"
              id="externalId"
              placeholder={adminStrings.locationsSection.editLocation.externalId}
              rules={{
                validate: (value: string) => {
                  const isDuplicate = locationsData?.locations?.some(
                    l => l.externalId === value && l?.externalId !== location.externalId
                  );
                  if (isDuplicate) return adminStrings.locationsSection.editLocation.uniqueId;
                  return true;
                }
              }}
              required
            />

            <SelectInputField
              name="holidayRegion"
              id="holidayRegion"
              label={adminStrings.locationsSection.editLocation.holidayRegion}
              options={holidayRegionSelectOptions}
            />

            <PhoneNumberField
              name={'phoneNumber'}
              id={'phoneNumber'}
              label={adminStrings.locationsSection.editLocation.phoneNumber}
            />

            <AddressField id="address" name="address" label={adminStrings.locationsSection.editLocation.address} />

            <div className="flex items-center justify-end gap-3">
              <SubmitCancelButtons
                disabled={!!methods.formState.errors.name?.message}
                onComplete={handleClose}
                loading={fetching}
                submitText={strings.common.save}
              />
            </div>
          </div>
        </TsForm>
      </SubmissionErrorBoundary>
    </TSTitleCard>
  );
};

export default AdminEditLocationDetailsCard;
